class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const wasOpen = this.classList.contains('active');
    if (!wasOpen && !this.openTimer) {
      this.restoreScrollLock = document.body.classList.contains('overflow-hidden');
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    document.dispatchEvent(new CustomEvent('crafii:cart-opening'));
    document.body.classList.add('overflow-hidden');
    this.classList.add('animate');
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.classList.add('active');
      const container = this.querySelector('.drawer__inner');
      clearTimeout(this.focusTimer);
      if (container) {
        const focus = () => {
          if (this.classList.contains('active') && !this.contains(document.activeElement)) {
            trapFocus(container, container);
          }
        };
        trapFocus(container, container);
        const duration = Math.max(...getComputedStyle(container).transitionDuration.split(',')
          .map((value) => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000)));
        this.focusTimer = setTimeout(focus, Math.min(duration + 50, 1000));
      }
      if (!wasOpen) this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    });
  }

  close() {
    clearTimeout(this.focusTimer);
    clearTimeout(this.openTimer);
    this.openTimer = null;
    this.renderRevision = (this.renderRevision || 0) + 1;
    this.classList.remove('active');
    const returnFocus = this.activeElement?.isConnected
      ? this.activeElement
      : document.getElementById('cart-icon-bubble');
    removeTrapFocus(returnFocus);
    if (!this.restoreScrollLock) document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    if (!parsedState || parsedState.status) return;
    const revision = this.renderRevision = (this.renderRevision || 0) + 1;
    const sections = this.getSectionsToRender();
    const parseSections = (contents) => sections.map((section) => {
      const html = contents?.[section.id];
      const source = html && new DOMParser().parseFromString(html, 'text/html')
        .querySelector(section.selector || '.shopify-section');
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      return source && target ? { source, target } : null;
    });
    try {
      let fragments = parseSections(parsedState.sections);
      if (fragments.some((fragment) => !fragment)) {
        const url = new URL(window.location.href);
        url.search = '';
        url.searchParams.set('sections', sections.map((section) => section.id).join(','));
        const response = await fetch(url);
        if (!response.ok) throw new Error('Unable to refresh cart sections');
        fragments = parseSections(await response.json());
      }
      if (revision !== this.renderRevision) return;
      if (fragments.some((fragment) => !fragment)) throw new Error('Missing cart sections');
      this.productId = parsedState.id;
      fragments.forEach(({ source, target }) => { target.innerHTML = source.innerHTML; });
      this.classList.remove('is-empty');
      this.open();
    } catch (error) {
      console.error('[Crafii] Cart drawer refresh failed', error);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element && !this.contains(element)
      ? element
      : document.getElementById('cart-icon-bubble');
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
